/**
 * Admin service (Supabase-backed).
 *
 * Kept under the legacy `firebaseAdminService` name so every consumer
 * page (Menu, Categories, Modifiers, AddOns, Users, Dashboard, etc.)
 * continues to compile without touching imports. All reads/writes go
 * to Supabase under the authenticated admin's tenant (RLS scopes the
 * rows automatically).
 *
 * Shape mapping notes:
 *   - Legacy `AdminMenuItem.category` is a free-text string. Supabase
 *     `menu_items.category_id` is a FK to `categories.id`. We resolve
 *     by name on write, expose the category name on read (joining
 *     `categories` so old call-sites keep working).
 *   - Theme management has no Supabase table yet; getter returns []
 *     and writers throw a clear "not implemented for Supabase" error.
 *   - `applies_to_size` (modifier groups) and `price_delta_by_size`
 *     (modifier options) aren't in the schema and are dropped on write
 *     / defaulted on read. The two Domino's-style flags can be added
 *     as a follow-up migration if the UI ever surfaces them again.
 */

import { supabase } from './supabaseClient';

export interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'admin' | 'manager' | 'cashier' | 'rider' | 'customer';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminOrder {
  id: string;
  order_number: string;
  customer_id: string;
  customer_name: string;
  total: number;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'delivered' | 'cancelled';
  items: any[];
  created_at: string;
  updated_at: string;
}

export interface AdminTheme {
  id: string;
  name: string;
  description?: string;
  app_type: 'pos' | 'admin' | 'customer';
  is_active: boolean;
  is_default: boolean;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
  created_at: string;
  updated_at: string;
}

export interface AdminMenuItem {
  id: string;
  name: string;
  name_de?: string;
  description?: string;
  description_de?: string;
  category: string;
  price: number;
  base_price?: number;
  price_with_tax?: number;
  tax_rate?: number;
  is_available: boolean;
  is_vegetarian?: boolean;
  is_vegan?: boolean;
  is_popular?: boolean;
  sold_out?: boolean;
  sold_out_at?: string;
  sold_out_by?: string;
  display_order?: number;
  display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null;
  image_url?: string;
  brand?: 'saheeh' | 'pizza_brand' | 'both' | null;
  modifier_group_ids?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface AdminCategory {
  id: string;
  name: string;
  icon?: string;
  image_url?: string;
  is_active: boolean;
  display_order?: number;
  display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null;
  created_at?: string;
  updated_at?: string;
}

export interface AdminAddOn {
  id: string;
  name: string;
  name_de?: string;
  price: number;
  description?: string;
  is_active: boolean;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface AdminModifierOption {
  id: string;
  group_id: string;
  name: string;
  name_de?: string;
  price_delta: number;
  price_delta_by_size?: { [sizeOptionId: string]: number };
  is_default: boolean;
  is_available: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface AdminModifierGroup {
  id: string;
  name: string;
  name_de?: string;
  type: 'single' | 'multi';
  required: boolean;
  min_select?: number;
  max_select?: number;
  applies_to_size: boolean;
  display_order: number;
  is_active: boolean;
  options?: AdminModifierOption[];
  created_at?: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------

async function getTenantId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  const tid = (user?.app_metadata as any)?.tenant_id || (user?.user_metadata as any)?.tenant_id;
  if (!tid) throw new Error('No tenant_id in JWT — admin user must belong to a tenant');
  return tid as string;
}

async function tryTenantId(): Promise<string | null> {
  try { return await getTenantId(); } catch { return null; }
}

/** Resolve a category by name (case-insensitive) under the current tenant. */
async function findCategoryByName(name: string): Promise<{ id: string; name: string } | null> {
  const tenant_id = await getTenantId();
  const { data } = await supabase
    .from('categories')
    .select('id, name')
    .eq('tenant_id', tenant_id)
    .ilike('name', name)
    .maybeSingle();
  return (data as any) || null;
}

async function ensureCategoryByName(name: string): Promise<string | null> {
  if (!name || name === 'Uncategorized') return null;
  const found = await findCategoryByName(name);
  if (found) return found.id;
  const tenant_id = await getTenantId();
  const { data, error } = await supabase
    .from('categories')
    .insert({ tenant_id, name, is_active: true, display_order: 0 })
    .select('id')
    .single();
  if (error) throw error;
  return (data as any).id;
}

function nameLocalToDe(name_local: any): string {
  return typeof name_local === 'string' ? name_local : '';
}

// ---------------------------------------------------------------------------

class FirebaseAdminService {
  private static instance: FirebaseAdminService;

  public static getInstance(): FirebaseAdminService {
    if (!FirebaseAdminService.instance) {
      FirebaseAdminService.instance = new FirebaseAdminService();
    }
    return FirebaseAdminService.instance;
  }

  // ==================== USERS ====================

  async getUsers(): Promise<AdminUser[]> {
    const { data, error } = await supabase
      .from('staff_users')
      .select('id, email, full_name, role, is_active, created_at, updated_at');
    if (error) {
      console.error('[ADMIN] getUsers:', error);
      return [];
    }
    return (data || []).map((u: any) => {
      const parts = String(u.full_name || '').trim().split(/\s+/);
      return {
        id: u.id,
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' '),
        email: u.email,
        role: u.role,
        is_active: !!u.is_active,
        created_at: u.created_at,
        updated_at: u.updated_at,
      } as AdminUser;
    });
  }

  async createUser(userData: Partial<AdminUser>): Promise<AdminUser> {
    const tenant_id = await getTenantId();
    const full_name = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || (userData.email || '');
    const { data, error } = await supabase
      .from('staff_users')
      .insert({
        tenant_id,
        email: userData.email,
        full_name,
        role: userData.role || 'cashier',
        is_active: userData.is_active !== false,
      })
      .select('id, email, full_name, role, is_active, created_at, updated_at')
      .single();
    if (error) throw error;
    const u = data as any;
    const parts = String(u.full_name || '').trim().split(/\s+/);
    return {
      id: u.id, email: u.email,
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' '),
      role: u.role, is_active: !!u.is_active,
      created_at: u.created_at, updated_at: u.updated_at,
    } as AdminUser;
  }

  async updateUser(userId: string, userData: Partial<AdminUser>): Promise<void> {
    const patch: any = { updated_at: new Date().toISOString() };
    if (userData.first_name !== undefined || userData.last_name !== undefined) {
      patch.full_name = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
    }
    if (userData.email !== undefined) patch.email = userData.email;
    if (userData.role !== undefined) patch.role = userData.role;
    if (userData.is_active !== undefined) patch.is_active = userData.is_active;
    const { error } = await supabase.from('staff_users').update(patch).eq('id', userId);
    if (error) throw error;
  }

  async deleteUser(userId: string): Promise<void> {
    const { error } = await supabase.from('staff_users').delete().eq('id', userId);
    if (error) throw error;
  }

  // ==================== ORDERS ====================

  async getOrders(): Promise<AdminOrder[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('id, order_number, customer_snapshot, grand_total, status, created_at, updated_at')
      .order('created_at', { ascending: false })
      .limit(500);
    if (error) {
      console.error('[ADMIN] getOrders:', error);
      return [];
    }
    return (data || []).map((o: any) => ({
      id: o.id,
      order_number: o.order_number || '',
      customer_id: '',
      customer_name: o.customer_snapshot?.name || '',
      total: Number(o.grand_total) || 0,
      status: o.status,
      items: [],
      created_at: o.created_at,
      updated_at: o.updated_at,
    } as AdminOrder));
  }

  async updateOrderStatus(orderId: string, status: AdminOrder['status']): Promise<void> {
    const { error } = await supabase
      .from('orders')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) throw error;
  }

  // ==================== THEMES (not yet schema-backed) ====================

  async getThemes(): Promise<AdminTheme[]> { return []; }
  async createTheme(_t: Partial<AdminTheme>): Promise<AdminTheme> {
    throw new Error('Theme management not yet wired to Supabase — coming in a follow-up migration');
  }
  async updateTheme(_id: string, _t: Partial<AdminTheme>): Promise<void> {
    throw new Error('Theme management not yet wired to Supabase');
  }
  async setActiveTheme(_id: string, _appType?: string): Promise<void> {
    throw new Error('Theme management not yet wired to Supabase');
  }
  async deleteTheme(_id: string): Promise<void> {
    throw new Error('Theme management not yet wired to Supabase');
  }

  // ==================== MENU ITEMS ====================

  async getMenuItems(): Promise<AdminMenuItem[]> {
    const { data, error } = await supabase
      .from('menu_items')
      .select('id, name, name_local, description, description_local, base_price, vat_rate, is_available, image_url, display_order, category_id, tags, created_at, updated_at, categories ( name )')
      .order('display_order', { ascending: true });
    if (error) {
      console.error('[ADMIN] getMenuItems:', error);
      throw error;
    }
    return (data || []).map((r: any) => {
      const vatRate = Number(r.vat_rate) || 0;
      const base = Number(r.base_price) || 0;
      return {
        id: r.id,
        name: r.name,
        name_de: nameLocalToDe(r.name_local),
        description: r.description || '',
        description_de: r.description_local || '',
        category: r.categories?.name || 'Uncategorized',
        price: Number((base * (1 + vatRate)).toFixed(2)),
        base_price: base,
        price_with_tax: Number((base * (1 + vatRate)).toFixed(2)),
        tax_rate: Math.round(vatRate * 100),
        is_available: r.is_available !== false,
        is_vegetarian: Array.isArray(r.tags) && r.tags.includes('vegetarian'),
        is_vegan: Array.isArray(r.tags) && r.tags.includes('vegan'),
        is_popular: Array.isArray(r.tags) && r.tags.includes('popular'),
        sold_out: !r.is_available,
        display_order: r.display_order ?? 999999,
        image_url: r.image_url || '',
        created_at: r.created_at,
        updated_at: r.updated_at,
      } as AdminMenuItem;
    });
  }

  async getMenuItemById(itemId: string): Promise<AdminMenuItem | null> {
    const { data, error } = await supabase
      .from('menu_items')
      .select('id, name, name_local, description, description_local, base_price, vat_rate, is_available, image_url, display_order, category_id, tags, created_at, updated_at, categories ( name )')
      .eq('id', itemId)
      .maybeSingle();
    if (error || !data) return null;
    const r: any = data;
    const vatRate = Number(r.vat_rate) || 0;
    const base = Number(r.base_price) || 0;
    return {
      id: r.id,
      name: r.name,
      name_de: nameLocalToDe(r.name_local),
      description: r.description || '',
      description_de: r.description_local || '',
      category: r.categories?.name || 'Uncategorized',
      price: Number((base * (1 + vatRate)).toFixed(2)),
      base_price: base,
      price_with_tax: Number((base * (1 + vatRate)).toFixed(2)),
      tax_rate: Math.round(vatRate * 100),
      is_available: r.is_available !== false,
      image_url: r.image_url || '',
      created_at: r.created_at,
      updated_at: r.updated_at,
    } as AdminMenuItem;
  }

  async createMenuItem(itemData: Partial<AdminMenuItem>): Promise<AdminMenuItem> {
    const tenant_id = await getTenantId();
    const taxRate = itemData.tax_rate ?? 15;          // SA default
    const vatRateDec = taxRate / 100;
    let basePrice: number;
    if (itemData.price_with_tax !== undefined) {
      basePrice = Number(itemData.price_with_tax) / (1 + vatRateDec);
    } else if (itemData.base_price !== undefined) {
      basePrice = Number(itemData.base_price);
    } else if (itemData.price !== undefined) {
      basePrice = Number(itemData.price) / (1 + vatRateDec);
    } else {
      basePrice = 0;
    }
    const category_id = itemData.category ? await ensureCategoryByName(itemData.category) : null;
    const tags: string[] = [];
    if (itemData.is_vegetarian) tags.push('vegetarian');
    if (itemData.is_vegan) tags.push('vegan');
    if (itemData.is_popular) tags.push('popular');
    const { data, error } = await supabase
      .from('menu_items')
      .insert({
        tenant_id,
        category_id,
        name: itemData.name || '',
        name_local: itemData.name_de || null,
        description: itemData.description || null,
        description_local: itemData.description_de || null,
        base_price: Number(basePrice.toFixed(4)),
        vat_rate: vatRateDec,
        is_available: itemData.is_available !== false,
        image_url: itemData.image_url || null,
        display_order: itemData.display_order ?? 0,
        tags,
      })
      .select('id, created_at, updated_at')
      .single();
    if (error) throw error;
    return {
      ...(itemData as any),
      id: (data as any).id,
      base_price: basePrice,
      price_with_tax: basePrice * (1 + vatRateDec),
      tax_rate: taxRate,
      created_at: (data as any).created_at,
      updated_at: (data as any).updated_at,
    } as AdminMenuItem;
  }

  async updateMenuItem(itemId: string, itemData: Partial<AdminMenuItem>): Promise<void> {
    const patch: any = { updated_at: new Date().toISOString() };
    if (itemData.name !== undefined) patch.name = itemData.name;
    if (itemData.name_de !== undefined) patch.name_local = itemData.name_de || null;
    if (itemData.description !== undefined) patch.description = itemData.description;
    if (itemData.description_de !== undefined) patch.description_local = itemData.description_de || null;
    if (itemData.is_available !== undefined) patch.is_available = itemData.is_available;
    if (itemData.image_url !== undefined) patch.image_url = itemData.image_url || null;
    if (itemData.display_order !== undefined) patch.display_order = itemData.display_order;
    if (itemData.category !== undefined) {
      patch.category_id = await ensureCategoryByName(itemData.category);
    }

    // Price handling — if any price field is given, recompute base + vat.
    if (itemData.tax_rate !== undefined) patch.vat_rate = Number(itemData.tax_rate) / 100;
    const vatRateDec = patch.vat_rate
      ?? (itemData.tax_rate !== undefined ? itemData.tax_rate / 100 : undefined);

    if (itemData.price_with_tax !== undefined && vatRateDec !== undefined) {
      patch.base_price = Number((Number(itemData.price_with_tax) / (1 + vatRateDec)).toFixed(4));
    } else if (itemData.base_price !== undefined) {
      patch.base_price = Number(Number(itemData.base_price).toFixed(4));
    } else if (itemData.price !== undefined && vatRateDec !== undefined) {
      patch.base_price = Number((Number(itemData.price) / (1 + vatRateDec)).toFixed(4));
    }

    // Tag flags — only rewrite tags if any flag is in the patch
    if (
      itemData.is_vegetarian !== undefined ||
      itemData.is_vegan !== undefined ||
      itemData.is_popular !== undefined
    ) {
      // Fetch existing tags so we keep unrelated ones
      const { data: existing } = await supabase
        .from('menu_items').select('tags').eq('id', itemId).maybeSingle();
      const tagSet = new Set<string>(((existing as any)?.tags as string[]) || []);
      const setFlag = (flag: string, on: boolean | undefined) => {
        if (on === true) tagSet.add(flag);
        else if (on === false) tagSet.delete(flag);
      };
      setFlag('vegetarian', itemData.is_vegetarian);
      setFlag('vegan', itemData.is_vegan);
      setFlag('popular', itemData.is_popular);
      patch.tags = Array.from(tagSet);
    }

    const { error } = await supabase.from('menu_items').update(patch).eq('id', itemId);
    if (error) throw error;
  }

  async deleteMenuItem(itemId: string): Promise<void> {
    const { error } = await supabase.from('menu_items').delete().eq('id', itemId);
    if (error) throw error;
  }

  // ==================== CATEGORIES ====================

  async getCategories(): Promise<AdminCategory[]> {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, image_url, is_active, display_order, created_at, updated_at')
      .order('display_order', { ascending: true });
    if (error) {
      console.error('[ADMIN] getCategories:', error);
      throw error;
    }
    return (data || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      icon: '🍽️',
      image_url: c.image_url || '',
      is_active: c.is_active !== false,
      display_order: c.display_order ?? 0,
      created_at: c.created_at,
      updated_at: c.updated_at,
    } as AdminCategory));
  }

  async createCategory(categoryData: Partial<AdminCategory>): Promise<AdminCategory> {
    const tenant_id = await getTenantId();
    const { data, error } = await supabase
      .from('categories')
      .insert({
        tenant_id,
        name: categoryData.name || '',
        image_url: categoryData.image_url || null,
        is_active: categoryData.is_active !== false,
        display_order: categoryData.display_order ?? 0,
      })
      .select('id, name, image_url, is_active, display_order, created_at, updated_at')
      .single();
    if (error) throw error;
    const c: any = data;
    return {
      id: c.id, name: c.name, icon: '🍽️',
      image_url: c.image_url || '',
      is_active: c.is_active !== false,
      display_order: c.display_order ?? 0,
      created_at: c.created_at, updated_at: c.updated_at,
    } as AdminCategory;
  }

  async updateCategory(categoryId: string, categoryData: Partial<AdminCategory>): Promise<void> {
    const patch: any = { updated_at: new Date().toISOString() };
    if (categoryData.name !== undefined) patch.name = categoryData.name;
    if (categoryData.image_url !== undefined) patch.image_url = categoryData.image_url || null;
    if (categoryData.is_active !== undefined) patch.is_active = categoryData.is_active;
    if (categoryData.display_order !== undefined) patch.display_order = categoryData.display_order;
    const { error } = await supabase.from('categories').update(patch).eq('id', categoryId);
    if (error) throw error;
  }

  async deleteCategory(categoryId: string): Promise<void> {
    const { error } = await supabase.from('categories').delete().eq('id', categoryId);
    if (error) throw error;
  }

  // ==================== ADDONS ====================

  async getAddOns(): Promise<AdminAddOn[]> {
    const { data, error } = await supabase
      .from('addons')
      .select('id, name, name_local, price, vat_rate, is_available, created_at')
      .order('name', { ascending: true });
    if (error) {
      console.error('[ADMIN] getAddOns:', error);
      throw error;
    }
    return (data || []).map((a: any, idx: number) => ({
      id: a.id,
      name: a.name,
      name_de: nameLocalToDe(a.name_local),
      price: Number(a.price) || 0,
      description: '',
      is_active: a.is_available !== false,
      sort_order: idx,
      created_at: a.created_at,
      updated_at: a.created_at,
    } as AdminAddOn));
  }

  async createAddOn(addonData: Partial<AdminAddOn>): Promise<AdminAddOn> {
    const tenant_id = await getTenantId();
    const { data, error } = await supabase
      .from('addons')
      .insert({
        tenant_id,
        name: addonData.name || '',
        name_local: addonData.name_de || null,
        price: Number(addonData.price) || 0,
        vat_rate: 0.15,
        is_available: addonData.is_active !== false,
      })
      .select('id, created_at')
      .single();
    if (error) throw error;
    return {
      id: (data as any).id,
      name: addonData.name || '',
      name_de: addonData.name_de,
      price: Number(addonData.price) || 0,
      description: addonData.description || '',
      is_active: addonData.is_active !== false,
      sort_order: addonData.sort_order ?? 0,
      created_at: (data as any).created_at,
      updated_at: (data as any).created_at,
    } as AdminAddOn;
  }

  async updateAddOn(addonId: string, addonData: Partial<AdminAddOn>): Promise<void> {
    const patch: any = {};
    if (addonData.name !== undefined) patch.name = addonData.name;
    if (addonData.name_de !== undefined) patch.name_local = addonData.name_de || null;
    if (addonData.price !== undefined) patch.price = Number(addonData.price);
    if (addonData.is_active !== undefined) patch.is_available = addonData.is_active;
    const { error } = await supabase.from('addons').update(patch).eq('id', addonId);
    if (error) throw error;
  }

  async deleteAddOn(addonId: string): Promise<void> {
    const { error } = await supabase.from('addons').delete().eq('id', addonId);
    if (error) throw error;
  }

  // ==================== MODIFIER GROUPS ====================

  async getModifierGroups(): Promise<AdminModifierGroup[]> {
    const { data, error } = await supabase
      .from('modifier_groups')
      .select('id, name, name_local, selection_mode, min_selections, max_selections, is_required, created_at, updated_at');
    if (error) {
      console.error('[ADMIN] getModifierGroups:', error);
      return [];
    }
    return (data || []).map((g: any, idx: number) => ({
      id: g.id,
      name: g.name,
      name_de: nameLocalToDe(g.name_local),
      type: g.selection_mode === 'multi' ? 'multi' : 'single',
      required: !!g.is_required,
      min_select: g.min_selections ?? undefined,
      max_select: g.max_selections ?? undefined,
      applies_to_size: false,
      display_order: idx,
      is_active: true,
      created_at: g.created_at,
      updated_at: g.updated_at,
    } as AdminModifierGroup));
  }

  async getModifierGroupWithOptions(groupId: string): Promise<AdminModifierGroup | null> {
    const { data: g } = await supabase
      .from('modifier_groups')
      .select('id, name, name_local, selection_mode, min_selections, max_selections, is_required, created_at, updated_at')
      .eq('id', groupId)
      .maybeSingle();
    if (!g) return null;
    const { data: opts } = await supabase
      .from('modifier_options')
      .select('id, modifier_group_id, name, name_local, price_delta, display_order, is_default, is_available, created_at')
      .eq('modifier_group_id', groupId)
      .order('display_order', { ascending: true });
    const gAny: any = g;
    return {
      id: gAny.id,
      name: gAny.name,
      name_de: nameLocalToDe(gAny.name_local),
      type: gAny.selection_mode === 'multi' ? 'multi' : 'single',
      required: !!gAny.is_required,
      min_select: gAny.min_selections ?? undefined,
      max_select: gAny.max_selections ?? undefined,
      applies_to_size: false,
      display_order: 0,
      is_active: true,
      options: (opts || []).map((o: any) => ({
        id: o.id,
        group_id: o.modifier_group_id,
        name: o.name,
        name_de: nameLocalToDe(o.name_local),
        price_delta: Number(o.price_delta) || 0,
        is_default: !!o.is_default,
        is_available: o.is_available !== false,
        display_order: o.display_order ?? 0,
        created_at: o.created_at,
      })),
      created_at: gAny.created_at,
      updated_at: gAny.updated_at,
    } as AdminModifierGroup;
  }

  async createModifierGroup(data: Partial<AdminModifierGroup>): Promise<AdminModifierGroup> {
    const tenant_id = await getTenantId();
    const { data: row, error } = await supabase
      .from('modifier_groups')
      .insert({
        tenant_id,
        name: data.name || '',
        name_local: data.name_de || null,
        selection_mode: data.type === 'multi' ? 'multi' : 'single',
        min_selections: data.min_select ?? 0,
        max_selections: data.max_select ?? null,
        is_required: !!data.required,
      })
      .select('id, created_at, updated_at')
      .single();
    if (error) throw error;
    return {
      id: (row as any).id,
      name: data.name || '',
      name_de: data.name_de,
      type: data.type === 'multi' ? 'multi' : 'single',
      required: !!data.required,
      min_select: data.min_select,
      max_select: data.max_select,
      applies_to_size: !!data.applies_to_size,
      display_order: data.display_order ?? 0,
      is_active: data.is_active !== false,
      created_at: (row as any).created_at,
      updated_at: (row as any).updated_at,
    } as AdminModifierGroup;
  }

  async updateModifierGroup(groupId: string, data: Partial<AdminModifierGroup>): Promise<void> {
    const patch: any = { updated_at: new Date().toISOString() };
    if (data.name !== undefined) patch.name = data.name;
    if (data.name_de !== undefined) patch.name_local = data.name_de || null;
    if (data.type !== undefined) patch.selection_mode = data.type;
    if (data.required !== undefined) patch.is_required = data.required;
    if (data.min_select !== undefined) patch.min_selections = data.min_select;
    if (data.max_select !== undefined) patch.max_selections = data.max_select;
    const { error } = await supabase.from('modifier_groups').update(patch).eq('id', groupId);
    if (error) throw error;
  }

  async deleteModifierGroup(groupId: string): Promise<void> {
    // FK is `on delete cascade`, so deleting the group removes its options.
    const { error } = await supabase.from('modifier_groups').delete().eq('id', groupId);
    if (error) throw error;
  }

  async createModifierOption(data: Partial<AdminModifierOption>): Promise<AdminModifierOption> {
    if (!data.group_id) throw new Error('group_id required');
    const tenant_id = await getTenantId();
    const { data: row, error } = await supabase
      .from('modifier_options')
      .insert({
        tenant_id,
        modifier_group_id: data.group_id,
        name: data.name || '',
        name_local: data.name_de || null,
        price_delta: Number(data.price_delta) || 0,
        display_order: data.display_order ?? 0,
        is_default: !!data.is_default,
        is_available: data.is_available !== false,
      })
      .select('id, created_at')
      .single();
    if (error) throw error;
    return {
      id: (row as any).id,
      group_id: data.group_id,
      name: data.name || '',
      name_de: data.name_de,
      price_delta: Number(data.price_delta) || 0,
      is_default: !!data.is_default,
      is_available: data.is_available !== false,
      display_order: data.display_order ?? 0,
      created_at: (row as any).created_at,
    } as AdminModifierOption;
  }

  async updateModifierOption(optionId: string, data: Partial<AdminModifierOption>): Promise<void> {
    const patch: any = {};
    if (data.name !== undefined) patch.name = data.name;
    if (data.name_de !== undefined) patch.name_local = data.name_de || null;
    if (data.price_delta !== undefined) patch.price_delta = Number(data.price_delta);
    if (data.is_default !== undefined) patch.is_default = data.is_default;
    if (data.is_available !== undefined) patch.is_available = data.is_available;
    if (data.display_order !== undefined) patch.display_order = data.display_order;
    const { error } = await supabase.from('modifier_options').update(patch).eq('id', optionId);
    if (error) throw error;
  }

  async deleteModifierOption(optionId: string): Promise<void> {
    const { error } = await supabase.from('modifier_options').delete().eq('id', optionId);
    if (error) throw error;
  }

  async getAllModifierOptions(): Promise<AdminModifierOption[]> {
    const { data, error } = await supabase
      .from('modifier_options')
      .select('id, modifier_group_id, name, name_local, price_delta, display_order, is_default, is_available, created_at');
    if (error) return [];
    return (data || []).map((o: any) => ({
      id: o.id,
      group_id: o.modifier_group_id,
      name: o.name,
      name_de: nameLocalToDe(o.name_local),
      price_delta: Number(o.price_delta) || 0,
      is_default: !!o.is_default,
      is_available: o.is_available !== false,
      display_order: o.display_order ?? 0,
    } as AdminModifierOption));
  }

  // ==================== DASHBOARD ====================

  async getDashboardStats(): Promise<{
    totalOrders: number;
    totalRevenue: number;
    totalUsers: number;
    activeRiders: number;
  }> {
    const tid = await tryTenantId();
    const [ordersRes, staffRes, ridersRes] = await Promise.all([
      supabase.from('orders').select('id, grand_total'),
      supabase.from('staff_users').select('id'),
      supabase.from('drivers').select('id, is_active').eq('is_active', true),
    ]);
    void tid;
    const orders = (ordersRes.data || []) as any[];
    const totalRevenue = orders.reduce((s, o) => s + (Number(o.grand_total) || 0), 0);
    return {
      totalOrders: orders.length,
      totalRevenue,
      totalUsers: (staffRes.data || []).length,
      activeRiders: (ridersRes.data || []).length,
    };
  }
}

export const firebaseAdminService = FirebaseAdminService.getInstance();
export default firebaseAdminService;
